/**
 * Lightweight fuzzy subsequence matcher with scoring + highlight ranges.
 * Zero dependencies. Not as smart as fuse.js but good enough for list filters.
 */

export interface FuzzyResult<T> {
	item: T;
	score: number;
	matches: number[]; // character indices in `key` that matched
	key: string;
}

/**
 * Score a subsequence match. Lower is better (like fzf).
 * Consecutive matches boost the score; gaps and match distance from start penalize.
 */
function scoreMatch(key: string, query: string): { score: number; matches: number[] } | null {
	if (!query) return { score: 0, matches: [] };
	const lowerKey = key.toLowerCase();
	const lowerQuery = query.toLowerCase();

	const matches: number[] = [];
	let keyIdx = 0;
	let lastMatchIdx = -2;
	let score = 0;

	for (let qi = 0; qi < lowerQuery.length; qi++) {
		const char = lowerQuery[qi];
		if (!char) continue;
		const found = lowerKey.indexOf(char, keyIdx);
		if (found === -1) return null;

		// consecutive bonus
		if (found === lastMatchIdx + 1) score -= 10;
		// start-of-word bonus
		if (found === 0 || lowerKey[found - 1] === " " || lowerKey[found - 1] === "-") score -= 5;
		// gap penalty
		score += found - keyIdx;

		matches.push(found);
		lastMatchIdx = found;
		keyIdx = found + 1;
	}

	return { score, matches };
}

export function fuzzyFilter<T>(
	items: T[],
	query: string,
	getKey: (item: T) => string,
): FuzzyResult<T>[] {
	if (!query.trim()) {
		return items.map((item) => ({
			item,
			score: 0,
			matches: [],
			key: getKey(item),
		}));
	}

	const results: FuzzyResult<T>[] = [];
	for (const item of items) {
		const key = getKey(item);
		const match = scoreMatch(key, query.trim());
		if (match) results.push({ item, score: match.score, matches: match.matches, key });
	}
	results.sort((a, b) => a.score - b.score);
	return results;
}

/**
 * Render highlighted text with <mark> around matched character positions.
 * Returns a React-friendly array of string segments.
 */
export function highlightMatches(
	text: string,
	matches: number[],
): Array<{ text: string; highlighted: boolean }> {
	if (matches.length === 0) return [{ text, highlighted: false }];

	const segments: Array<{ text: string; highlighted: boolean }> = [];
	let cursor = 0;
	const matchSet = new Set(matches);

	while (cursor < text.length) {
		const isMatch = matchSet.has(cursor);
		let end = cursor + 1;
		while (end < text.length && matchSet.has(end) === isMatch) end++;
		segments.push({ text: text.slice(cursor, end), highlighted: isMatch });
		cursor = end;
	}

	return segments;
}
